import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import ResetPasswordButton from './ResetPasswordButton';
import RevealWmmPasswordButton from './RevealWmmPasswordButton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * User tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function UsersTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  // "Login as" — swap this tab into an impersonated member session. The
  // admin token is parked in sessionStorage so the AppShell banner can
  // restore it via "Return to Admin".
  const impersonateUser = async (u) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/users/${u.id}/impersonate`,
        {},
        { headers: getAuthHeader() },
      );
      sessionStorage.setItem('impersonation_return_token', localStorage.getItem('token') || '');
      sessionStorage.setItem('impersonation_user', JSON.stringify({ name: u.name, email: u.email }));
      localStorage.setItem('token', res.data.token);
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impersonation failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-playfair font-bold text-foreground">User Management</h2>
                    <div className="flex gap-4 items-center">
                      <span className="text-muted-foreground">Total Users: {users.length}</span>
                      <Link to="/admin/add-user">
                        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-user-btn">
                          Add New User
                        </Button>
                      </Link>
                    </div>
                  </div>
            
                  {/* Search */}
                  <div className="mb-4">
                    <Input
                      placeholder="Search by name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="max-w-md bg-input text-foreground"
                    />
                  </div>
            
                  <div className="space-y-3">
                    {filteredUsers.map((u) => (
                      <div key={u.id} className={`wm-card p-4 rounded-lg ${u.is_suspended ? 'border-2 border-red-500/50 bg-red-900/10' : ''}`} data-testid="user-item">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-foreground">{u.name}</h3>
                              {u.is_suspended && (
                                <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400 flex items-center gap-1">
                                  <Ban className="w-3 h-3" /> SUSPENDED
                                </span>
                              )}
                              {u.is_archived && (
                                <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400 flex items-center gap-1">
                                  <Archive className="w-3 h-3" /> ARCHIVED
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className={`text-xs px-2 py-1 rounded capitalize ${
                                u.membership_tier === 'founder' ? 'bg-amber-500/20 text-amber-400' :
                                u.membership_tier === 'growth' ? 'bg-purple-500/20 text-purple-400' :
                                u.membership_tier === 'ascend' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {u.membership_tier || 'No membership'}
                              </span>
                              {u.is_founder && (
                                <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400 flex items-center gap-1">
                                  <Crown className="w-3 h-3" /> Founder
                                </span>
                              )}
                              {u.is_admin && (
                                <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                                  Admin
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Sales: ${(u.sales_total || 0).toLocaleString()} | Referral: {u.referral_code} | Joined: {new Date(u.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            {/* Tier Change */}
                            <select
                              value={u.membership_tier || 'none'}
                              onChange={(e) => changeUserTier(u.id, e.target.value)}
                              className="text-xs px-2 py-1 rounded bg-input border border-border text-foreground"
                              disabled={u.is_admin}
                            >
                              <option value="none">No Tier</option>
                              <option value="ascend">Ascend</option>
                              <option value="growth">Growth</option>
                              <option value="founder">Founder</option>
                            </select>
                      
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                onClick={() => toggleFoundingMember(u.id)}
                                className={u.is_founder ? 'bg-amber-600 hover:bg-amber-700 text-xs' : 'bg-slate-600 hover:bg-slate-700 text-xs'}
                                data-testid="toggle-founder-btn"
                              >
                                {u.is_founder ? <Crown className="w-3 h-3" /> : <Crown className="w-3 h-3" />}
                              </Button>
                        
                              {!u.is_admin && (
                                <Button
                                  size="sm"
                                  onClick={() => suspendUser(u.id, !u.is_suspended)}
                                  className={u.is_suspended ? 'bg-green-600 hover:bg-green-700 text-xs' : 'bg-red-600 hover:bg-red-700 text-xs'}
                                  data-testid="suspend-btn"
                                  title={u.is_suspended ? 'Unsuspend User' : 'Suspend User'}
                                >
                                  {u.is_suspended ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                                </Button>
                              )}

                              {!u.is_admin && (
                                <Button
                                  size="sm"
                                  onClick={() => impersonateUser(u)}
                                  className="bg-indigo-600 hover:bg-indigo-700 text-xs"
                                  data-testid={`impersonate-user-${u.id}`}
                                  title="Login as this user"
                                >
                                  <LogIn className="w-3 h-3" />
                                </Button>
                              )}

                              {!u.is_admin && (
                                <ResetPasswordButton user={u} getAuthHeader={getAuthHeader} />
                              )}

                              {!u.is_admin && u.is_founder && (
                                <RevealWmmPasswordButton user={u} getAuthHeader={getAuthHeader} />
                              )}

                              {!u.is_admin && (
                                <Button
                                  size="sm"
                                  onClick={() => archiveUser(u.id, !u.is_archived)}
                                  className={u.is_archived ? 'bg-amber-600 hover:bg-amber-700 text-xs' : 'bg-slate-600 hover:bg-slate-700 text-xs'}
                                  data-testid="archive-btn"
                                  title={u.is_archived ? 'Unarchive User' : 'Archive User'}
                                >
                                  <Archive className="w-3 h-3" />
                                </Button>
                              )}

                              {!u.is_admin && (
                                <Button
                                  size="sm"
                                  onClick={() => permanentlyDeleteUser(u.id, u.name)}
                                  className="bg-red-700 hover:bg-red-800 text-xs"
                                  data-testid="delete-user-btn"
                                  title="Permanently delete user"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
    </div>
  );
}
